import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import userService from "./userService";

/**
 * =====================================================
 * ERROR HELPER
 * =====================================================
 */

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.message || error?.message || "Er ging iets fout"
  );
};

/**
 * =====================================================
 * HELPERS
 * =====================================================
 */

const upsertUser = (users, updatedUser) => {
  if (!updatedUser?._id) {
    return users;
  }

  const index = users.findIndex((user) => user._id === updatedUser._id);

  if (index === -1) {
    return [updatedUser, ...users];
  }

  const clonedUsers = [...users];

  clonedUsers[index] = updatedUser;

  return clonedUsers;
};

const syncSelectedUser = (state, updatedUser) => {
  if (state.selectedUser?._id === updatedUser?._id) {
    state.selectedUser = updatedUser;
  }
};

/**
 * =====================================================
 * GET USERS
 * =====================================================
 */

export const getUsers = createAsyncThunk(
  "users/getUsers",

  async (params = {}, thunkAPI) => {
    try {
      return await userService.getUsers(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  },
);

/**
 * =====================================================
 * GET USER BY ID
 * =====================================================
 */

export const getUserById = createAsyncThunk(
  "users/getUserById",

  async (id, thunkAPI) => {
    try {
      return await userService.getUserById(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  },
);

/**
 * =====================================================
 * CREATE USER
 * =====================================================
 */

export const createUser = createAsyncThunk(
  "users/createUser",

  async (payload, thunkAPI) => {
    try {
      return await userService.createUser(payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  },
);

/**
 * =====================================================
 * UPDATE USER
 * =====================================================
 */

export const updateUser = createAsyncThunk(
  "users/updateUser",

  async ({ id, data }, thunkAPI) => {
    try {
      return await userService.updateUser({
        id,
        data,
      });
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  },
);

/**
 * =====================================================
 * UPDATE USER PERMISSIONS
 * =====================================================
 */

export const updateUserPermissions = createAsyncThunk(
  "users/updateUserPermissions",

  async ({ id, permissions }, thunkAPI) => {
    try {
      return await userService.updateUserPermissions({
        id,
        permissions,
      });
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  },
);

/**
 * =====================================================
 * CHANGE USER STATUS
 * =====================================================
 */

export const changeUserStatus = createAsyncThunk(
  "users/changeUserStatus",

  async ({ id, status }, thunkAPI) => {
    try {
      return await userService.changeUserStatus({
        id,
        status,
      });
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  },
);

/**
 * =====================================================
 * DISABLE USER
 * =====================================================
 */

export const disableUser = createAsyncThunk(
  "users/disableUser",

  async (id, thunkAPI) => {
    try {
      return await userService.disableUser(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  },
);

/**
 * =====================================================
 * RESEND SETUP INVITE
 * =====================================================
 */

export const resendSetupInvite = createAsyncThunk(
  "users/resendSetupInvite",

  async (id, thunkAPI) => {
    try {
      return await userService.resendSetupInvite(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  },
);

/**
 * =====================================================
 * GET MY ORGANIZATION COACHES
 * =====================================================
 */

export const getMyOrganizationCoaches = createAsyncThunk(
  "users/getMyOrganizationCoaches",

  async (_, thunkAPI) => {
    try {
      return await userService.getOrganizationCoaches();
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  },
);

export const getOrganizationCoaches = createAsyncThunk(
  "users/getOrganizationCoaches",

  async (organizationId, thunkAPI) => {
    try {
      return await userService.getOrganizationCoaches(organizationId);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error.message ||
          "Coaches laden mislukt",
      );
    }
  },
);

/**
 * =====================================================
 * INITIAL STATE
 * =====================================================
 */

const initialState = {
  users: [],

  organizationCoaches: [],

  selectedUser: null,

  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },

  filters: {
    search: "",

    role: "",

    status: "",

    auth_provider: "",

    organization: "",
  },

  isLoading: false,

  isCreating: false,

  isUpdating: false,

  isInviting: false,

  error: null,

  isFetchingCoaches: false,
};

/**
 * =====================================================
 * SLICE
 * =====================================================
 */

const userSlice = createSlice({
  name: "users",

  initialState,

  reducers: {
    clearUserError: (state) => {
      state.error = null;
    },

    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },

    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },

    setUserFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    resetUserFilters: (state) => {
      state.filters = initialState.filters;
    },

    resetUsersState: () => initialState,
  },

  extraReducers: (builder) => {
    builder

      /**
       * =====================================================
       * GET USERS
       * =====================================================
       */

      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;

        state.error = null;
      })

      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;

        const data = action.payload?.data;

        state.users = data?.items || [];

        state.pagination = data?.pagination || initialState.pagination;
      })

      .addCase(getUsers.rejected, (state, action) => {
        state.isLoading = false;

        state.error = action.payload || "Gebruikers laden mislukt.";
      })

      /**
       * =====================================================
       * GET USER BY ID
       * =====================================================
       */

      .addCase(getUserById.pending, (state) => {
        state.isLoading = true;

        state.error = null;
      })

      .addCase(getUserById.fulfilled, (state, action) => {
        state.isLoading = false;

        state.selectedUser = action.payload?.data || null;
      })

      .addCase(getUserById.rejected, (state, action) => {
        state.isLoading = false;

        state.error = action.payload || "Gebruiker laden mislukt.";
      })

      /**
       * =====================================================
       * CREATE USER
       * =====================================================
       */

      .addCase(createUser.pending, (state) => {
        state.isCreating = true;

        state.error = null;
      })

      .addCase(createUser.fulfilled, (state, action) => {
        state.isCreating = false;

        const createdUser = action.payload?.data;

        if (createdUser) {
          state.users = upsertUser(state.users, createdUser);
        }
      })

      .addCase(createUser.rejected, (state, action) => {
        state.isCreating = false;

        state.error = action.payload || "Gebruiker aanmaken mislukt.";
      })

      /**
       * =====================================================
       * UPDATE USER
       * =====================================================
       */

      .addCase(updateUser.pending, (state) => {
        state.isUpdating = true;

        state.error = null;
      })

      .addCase(updateUser.fulfilled, (state, action) => {
        state.isUpdating = false;

        const updatedUser = action.payload?.data;

        if (updatedUser) {
          state.users = upsertUser(state.users, updatedUser);

          syncSelectedUser(state, updatedUser);
        }
      })

      .addCase(updateUser.rejected, (state, action) => {
        state.isUpdating = false;

        state.error = action.payload || "Gebruiker wijzigen mislukt.";
      })

      /**
       * =====================================================
       * UPDATE USER PERMISSIONS
       * =====================================================
       */

      .addCase(updateUserPermissions.pending, (state) => {
        state.isUpdating = true;

        state.error = null;
      })

      .addCase(updateUserPermissions.fulfilled, (state, action) => {
        state.isUpdating = false;

        const updatedUser = action.payload?.data;

        if (updatedUser) {
          state.users = upsertUser(state.users, updatedUser);

          syncSelectedUser(state, updatedUser);
        }
      })

      .addCase(updateUserPermissions.rejected, (state, action) => {
        state.isUpdating = false;

        state.error = action.payload || "Rechten wijzigen mislukt.";
      })

      /**
       * =====================================================
       * CHANGE USER STATUS
       * =====================================================
       */

      .addCase(changeUserStatus.pending, (state) => {
        state.isUpdating = true;

        state.error = null;
      })

      .addCase(changeUserStatus.fulfilled, (state, action) => {
        state.isUpdating = false;

        const updatedUser = action.payload?.data;

        if (updatedUser) {
          state.users = upsertUser(state.users, updatedUser);

          syncSelectedUser(state, updatedUser);
        }
      })

      .addCase(changeUserStatus.rejected, (state, action) => {
        state.isUpdating = false;

        state.error = action.payload || "Status wijzigen mislukt.";
      })

      /**
       * =====================================================
       * DISABLE USER
       * =====================================================
       */

      .addCase(disableUser.pending, (state) => {
        state.isUpdating = true;

        state.error = null;
      })

      .addCase(disableUser.fulfilled, (state, action) => {
        state.isUpdating = false;

        const updatedUser = action.payload?.data;

        if (updatedUser) {
          state.users = upsertUser(state.users, updatedUser);

          syncSelectedUser(state, updatedUser);
        }
      })

      .addCase(disableUser.rejected, (state, action) => {
        state.isUpdating = false;

        state.error = action.payload || "Gebruiker deactiveren mislukt.";
      })

      /**
       * =====================================================
       * RESEND INVITE
       * =====================================================
       */

      .addCase(resendSetupInvite.pending, (state) => {
        state.isInviting = true;

        state.error = null;
      })

      .addCase(resendSetupInvite.fulfilled, (state) => {
        state.isInviting = false;
      })

      .addCase(resendSetupInvite.rejected, (state, action) => {
        state.isInviting = false;

        state.error = action.payload || "Uitnodiging verzenden mislukt.";
      })

      /**
       * =====================================================
       * GET MY ORGANIZATION COACHES
       * =====================================================
       */

      .addCase(getMyOrganizationCoaches.pending, (state) => {
        state.isLoading = true;

        state.error = null;
      })

      .addCase(getMyOrganizationCoaches.fulfilled, (state, action) => {
        state.isLoading = false;

        state.organizationCoaches = action.payload?.data || [];
      })

      .addCase(getMyOrganizationCoaches.rejected, (state, action) => {
        state.isLoading = false;

        state.error = action.payload || "Coaches laden mislukt.";
      })
      .addCase(getOrganizationCoaches.pending, (state) => {
        state.isFetchingCoaches = true;
      })

      .addCase(getOrganizationCoaches.fulfilled, (state, action) => {
        state.isFetchingCoaches = false;

        state.organizationCoaches = action.payload?.data || [];
      })

      .addCase(getOrganizationCoaches.rejected, (state, action) => {
        state.isFetchingCoaches = false;

        state.error = action.payload || "Coaches laden mislukt";
      });
  },
});

export const {
  clearUserError,
  setSelectedUser,
  clearSelectedUser,
  setUserFilters,
  resetUserFilters,
  resetUsersState,
} = userSlice.actions;

export default userSlice.reducer;
